import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, Subject } from 'rxjs';
import { ConfigService } from './config.service';

/**
 * Credentials pool — the user's named, reusable provider credentials, each bound to
 * agents via `credentials_id`. A Credential's `mode` (subscription vs api_key) is what
 * makes billing legible. Backed by the /credentials API over services/credentials.py.
 */
export namespace MuxCredentialService {
  export type Vendor = 'anthropic' | 'google' | 'openai' | 'local';
  export type Mode = 'subscription' | 'api_key' | 'local';
  export type Status = 'authed' | 'expiring' | 'expired' | 'unconfigured';

  export interface Credential {
    scoped_team?: string | null;   // team the credential lives in; null = unscoped (usable in all your teams)
    team_slot?: boolean;           // defined by the team policy: members sign in to it, admins manage it
    holder?: 'user' | 'team';      // team-held = admins hold the key; user = each member signs in
    required?: boolean;
    account_email?: string | null; // who it signs in as (from ~/.claude-{id}/.claude.json oauthAccount)
    account_org?: string | null;
    id: string | null;         // null == the flat/default credential
    name: string;
    vendor: Vendor;
    mode: Mode;
    status: Status;
    is_default: boolean;
    has_manifest: boolean;
  }

  export interface CredentialsResponse {
    credentials: Credential[];
  }

  export interface CreateCredentialRequest {
    id: string;
    name: string;
    vendor: Vendor;
    mode: Mode;
  }

  export interface UpdateCredentialRequest {
    name?: string;
    mode?: Mode;
    status?: Status;
    vendor?: Vendor;
  }

  export interface CredentialStatus {
    auth_status: string | null;
    error?: string | null;
    warning?: string | null;
    token_details?: {
      scopes?: string[]; expires_at?: number; expires_in?: string;
      login_expires_at?: number; login_expires_in?: string;
    };
  }
}

@Injectable({ providedIn: 'root' })
export class MuxCredentialService {
  /** Emits when the credential pool changes (create/rename/rotate/delete/auth) so
   *  other surfaces (the Agents lens) can refresh their credential list live. */
  public readonly changed$ = new Subject<void>();

  constructor(private http: HttpClient) {}

  /** List the credential pool (manifested + inferred legacy dirs + the default). */
  /** Scope an unscoped credential into a team (mirror of the agent move). */
  async scopeToTeam(cid: string, team: string): Promise<void> {
    await lastValueFrom(this.http.post(`${ConfigService.muxUrl}/credentials/${encodeURIComponent(cid)}/team`, { team }));
  }

  async list(team?: string): Promise<MuxCredentialService.Credential[]> {
    // Team-scoped credentials: an omitted team means "the team I'm standing in".
    if (team === undefined) {
      try { team = JSON.parse(localStorage.getItem('me') || '{}')?.selectedTeamName || 'local'; } catch { team = 'local'; }
    }
    const res = await lastValueFrom(
      this.http.get<MuxCredentialService.CredentialsResponse>(
        `${ConfigService.muxUrl}/credentials?team=${encodeURIComponent(team)}`
      )
    );
    return res?.credentials ?? [];
  }

  /** Create a Credential (writes its manifest). Secret is populated by the auth/configure flow. */
  async create(
    req: MuxCredentialService.CreateCredentialRequest,
    team: string = 'local'
  ): Promise<MuxCredentialService.Credential> {
    return await lastValueFrom(
      this.http.post<MuxCredentialService.Credential>(
        `${ConfigService.muxUrl}/credentials?team=${encodeURIComponent(team)}`,
        req
      )
    );
  }

  /** Patch a Credential manifest (name / mode / status / vendor). */
  async update(
    cid: string,
    patch: MuxCredentialService.UpdateCredentialRequest,
    team: string = 'local'
  ): Promise<MuxCredentialService.Credential> {
    return await lastValueFrom(
      this.http.patch<MuxCredentialService.Credential>(
        `${ConfigService.muxUrl}/credentials/${encodeURIComponent(cid)}?team=${encodeURIComponent(team)}`,
        patch
      )
    );
  }

  /** Delete a Credential and its material. */
  async remove(cid: string, team: string = 'local'): Promise<void> {
    await lastValueFrom(
      this.http.delete(
        `${ConfigService.muxUrl}/credentials/${encodeURIComponent(cid)}?team=${encodeURIComponent(team)}`
      )
    );
  }

  /** Fetch a credential's live auth status (token expiry, scopes) via the vendor's
   *  backend status endpoint. Returns null for vendors without a status path yet. */
  async status(
    cid: string | null,
    vendor: MuxCredentialService.Vendor,
    team: string = 'local',
  ): Promise<MuxCredentialService.CredentialStatus | null> {
    const backendId = vendor === 'anthropic' ? 'claude-cli' : null;
    if (!backendId) return null;
    let url = `${ConfigService.muxUrl}/backends/${backendId}/status?team=${encodeURIComponent(team)}`;
    if (cid) url += `&credentials_id=${encodeURIComponent(cid)}`;
    try {
      return await lastValueFrom(this.http.get<MuxCredentialService.CredentialStatus>(url));
    } catch {
      return null;
    }
  }

  /** Authenticate a metered Credential by storing its Anthropic API key. */
  async setApiKey(
    cid: string,
    apiKey: string,
    team: string = 'local'
  ): Promise<MuxCredentialService.Credential> {
    return await lastValueFrom(
      this.http.post<MuxCredentialService.Credential>(
        `${ConfigService.muxUrl}/credentials/${encodeURIComponent(cid)}/api-key?team=${encodeURIComponent(team)}`,
        { api_key: apiKey }
      )
    );
  }

  /** Authenticate OpenAI's api_key mode via Codex (`codex login --with-api-key`), which
   *  writes ~/.codex/auth.json — the same store team login uses. Ambient, not pool-scoped. */
  async codexLoginApiKey(apiKey: string, credentialsId?: string): Promise<{ success: boolean }> {
    const q = credentialsId ? `?credentials_id=${encodeURIComponent(credentialsId)}` : '';
    return await lastValueFrom(
      this.http.post<{ success: boolean }>(
        `${ConfigService.muxUrl}/backends/chatgpt/auth/api-key${q}`,
        { api_key: apiKey }
      )
    );
  }
}
